import axios from 'axios';

// Use relative URL for API calls - this works for both preview and production
// The Kubernetes ingress routes /api/* to the backend automatically
const API_URL = '/api';

// Export for components that use fetch directly
export const getApiUrl = () => API_URL;
export { API_URL };

// Create axios instance with credentials for httpOnly cookies
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,  // Send cookies with every request
});

// Named export for api instance (in addition to default export)
export { api };

// Handle auth errors - redirect to login on 401
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Don't redirect if already on login/set-password/public pages
      const p = window.location.pathname;
      if (!p.includes('/login') && !p.includes('/set-password') && !p.startsWith('/public/')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authApi = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  logout: () => api.post('/auth/logout'),
  register: (userData) => api.post('/users', userData),
  getMe: () => api.get('/auth/me'),
};

// User APIs
export const userApi = {
  getAll: () => api.get('/users'),
  getTechnicians: () => api.get('/users/technicians'),
  update: (userId, data) => api.put(`/users/${userId}`, data),
  delete: (userId) => api.delete(`/users/${userId}`),
  // Profile self-update
  updateMyProfile: (data) => api.put('/users/me/profile', data),
  // Fiscal settings
  getMyFiscalSettings: () => api.get('/users/me/fiscal-settings'),
  updateMyFiscalSettings: (data) => api.put('/users/me/fiscal-settings', data),
  getFiscalRegimes: () => api.get('/fiscal-regimes'),
};

// Lead APIs
export const leadApi = {
  getAll: (status) => api.get('/leads', { params: { status } }),
  getOne: (id) => api.get(`/leads/${id}`),
  create: (data) => {
    // Filter out empty strings and convert them to null for optional fields
    const cleanData = Object.fromEntries(
      Object.entries(data).map(([key, value]) => [key, value === '' ? null : value])
    );
    return api.post('/leads', cleanData);
  },
  update: (id, data) => {
    const cleanData = Object.fromEntries(
      Object.entries(data).filter(([key, value]) => value !== undefined).map(([key, value]) => [key, value === '' ? null : value])
    );
    return api.put(`/leads/${id}`, cleanData);
  },
  delete: (id) => api.delete(`/leads/${id}`),
  reassign: (id, data) => api.post(`/leads/${id}/reassign`, { technician_id: data.technician_id, reason: data.reason || null, updates: data.updates ?? null }),
  addNote: (id, text) => api.post(`/leads/${id}/notes`, { text }),
  deleteNote: (leadId, noteId) => api.delete(`/leads/${leadId}/notes/${noteId}`),
  requestAuthorization: (data) => api.post('/leads/request-authorization', data),
  // Transition API - usa new_status come campo
  transition: (id, data) => {
    const payload = { 
      new_status: data.to_status || data.new_status, 
      notes: data.notes || null,
      assigned_technician_id: data.assigned_technician_id || null
    };
    if (data.motivo_perdita !== undefined) {
      payload.motivo_perdita = data.motivo_perdita || null;
    }
    if (data.motivo_perdita_note !== undefined) {
      payload.motivo_perdita_note = data.motivo_perdita_note || null;
    }
    return api.post(`/leads/${id}/transition`, payload);
  },
  getTransitions: (id) => api.get(`/leads/${id}/transitions`),
  getFollowup: (id) => api.get(`/leads/${id}/followup`),
  setNextStep: (id, data) => api.post(`/leads/${id}/next-step`, data),
  completeNextStep: (id, data) => api.post(`/leads/${id}/next-step/complete`, data || {}),
  bulkRecover: (leadIds, notes) => api.post('/leads/bulk-recover', { lead_ids: leadIds, notes: notes || null }),
  exportLostCsv: () => api.get('/leads/export/lost-with-estimates', { responseType: 'blob' }),
};

// Appointment APIs
export const appointmentApi = {
  getAll: (params) => api.get('/appointments', { params }),
  getOne: (id) => api.get(`/appointments/${id}`),
  create: (data) => api.post('/appointments', data),
  update: (id, data) => api.put(`/appointments/${id}`, data),
  delete: (id) => api.delete(`/appointments/${id}`),
  cancel: (id, data) => api.post(`/appointments/${id}/cancel`, data),
  reschedule: (id, data) => api.post(`/appointments/${id}/reschedule`, data),
  // Email notifications
  sendConfirmation: (id) => api.post(`/appointments/${id}/send-confirmation`),
  sendModification: (id, data) => api.post(`/appointments/${id}/send-modification`, data),
  sendCancellation: (id, data) => api.post(`/appointments/${id}/send-cancellation`, data),
  sendReminder: (id) => api.post(`/appointments/${id}/send-reminder`),
  // Travel info
  getTravelInfo: (id) => api.get(`/appointments/${id}/travel-info`),
};

// Scheduler API (Admin only)
export const schedulerApi = {
  getStatus: () => api.get('/scheduler/status'),
  runReminders: () => api.post('/scheduler/run-reminders'),
};

// Price List APIs
export const priceListApi = {
  getAll: (section, category) => api.get('/price-list', { params: { section, category } }),
  getNextCode: (category, subcategory) => api.get(`/price-list/next-code/${category}`, { params: { subcategory } }),
  create: (data) => api.post('/price-list', data),
  update: (id, data) => api.put(`/price-list/${id}`, data),
  delete: (id) => api.delete(`/price-list/${id}`),
  // Conteggio preventivi che usano la voce (per dialog riclassifica)
  getUsageCount: (id) => api.get(`/price-list/${id}/usage-count`),
  // Inline update (singolo campo)
  updateInline: (id, field, value) => api.put(`/price-list/${id}/inline`, null, { params: { field, value } }),
  // Bulk delete
  bulkDelete: (itemIds) => api.post('/price-list/bulk-delete', itemIds),
  // Export CSV
  exportCSV: async (category = null) => {
    const response = await api.get('/price-list/export/csv', { 
      params: { category },
      responseType: 'blob'
    });
    // Trigger download
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `listino_prezzi_${category || 'completo'}_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    return response;
  },
  // Duplicates
  findDuplicates: (searchType = 'all', category = null) => 
    api.get('/price-list/duplicates', { params: { search_type: searchType, category } }),
  // Import
  previewImport: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/price-list/import/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  // Preview multi-foglio
  previewSheets: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/price-list/import/preview-sheets', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  // Import multi-foglio selettivo
  importSheets: (data) => api.post('/price-list/import/sheets', data),
  importFile: (file, mapping, categoriaDefault, sottocategoriaDefault, sovrascrivi) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('mapping', JSON.stringify(mapping));
    formData.append('categoria_default', categoriaDefault);
    formData.append('sottocategoria_default', sottocategoriaDefault);
    formData.append('sovrascrivi_esistenti', sovrascrivi);
    return api.post('/price-list/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
};

// Estimate Categories APIs
export const estimateCategoryApi = {
  getAll: () => api.get('/estimate-categories'),
  getSubcategories: (categoryCode) => api.get(`/estimate-categories/${categoryCode}/subcategories`),
};

// Promotions APIs
export const promotionApi = {
  getAll: (activeOnly = true) => api.get('/promotions', { params: { active_only: activeOnly } }),
  create: (data) => api.post('/promotions', data),
  update: (id, data) => api.put(`/promotions/${id}`, data),
  delete: (id) => api.delete(`/promotions/${id}`),
};

// Estimate APIs
export const electricalApi = {
  getConfig: () => api.get('/electrical/config'),
  preview: (payload) => api.post('/electrical/preview', payload),
  apply: (estimateId, payload) => api.post(`/estimates/${estimateId}/electrical/apply`, payload),
};

export const estimateApi = {
  getOpportunities: () => api.get('/estimates/opportunities'),
  getAll: (params) => api.get('/estimates', { params }),
  getByLead: (leadId) => api.get('/estimates', { params: { lead_id: leadId, visibility: 'all' } }),
  getOne: (id) => api.get(`/estimates/${id}`),
  create: (data) => api.post('/estimates', data),
  update: (id, data) => api.put(`/estimates/${id}`, data),
  reorderItems: (id, category, orderedItemIds) =>
    api.patch(`/estimates/${id}/items/reorder`, { category, ordered_item_ids: orderedItemIds }),
  validate: (id) => api.post(`/estimates/${id}/validate`),
  // Progettazione auto-insert
  addProgettazione: (id) => api.post(`/estimates/${id}/add-progettazione`),
  addProgettazioneToAll: () => api.post('/estimates/add-progettazione-to-all'),
  // Versioning
  createVersion: (id, versionNote) => api.post(`/estimates/${id}/create-version`, { version_note: versionNote }),
  getVersions: (id) => api.get(`/estimates/${id}/versions`),
  // Clone CROSS-LEAD (nuovo preventivo su un lead destinatario da un preventivo esistente)
  searchReusableForLead: (destinationLeadId, params) =>
    api.get(`/leads/${destinationLeadId}/estimates/reusable`, { params }),
  cloneFromExisting: (destinationLeadId, payload) =>
    api.post(`/leads/${destinationLeadId}/estimates/from-existing`, payload),
  // Suggerimenti automatici deterministici (score + reasons)
  suggestionsForLead: (destinationLeadId, params) =>
    api.get(`/leads/${destinationLeadId}/estimates/suggestions`, { params }),
  // Preview repricing (dry-run, no insert)
  previewCloneFromExisting: (destinationLeadId, payload) =>
    api.post(`/leads/${destinationLeadId}/estimates/from-existing/preview`, payload),
  // PDF Generation (Legacy)
  downloadPdfFull: async (id) => {
    const response = await api.get(`/estimates/${id}/pdf/full`, { responseType: 'blob' });
    return response;
  },
  downloadPdfClient: async (id) => {
    const response = await api.get(`/estimates/${id}/pdf/client`, { responseType: 'blob' });
    return response;
  },
  // NEW Document Engine APIs
  generateDocument: (id, renderMode = 'customer') => 
    api.post(`/documents/estimates/${id}/generate?render_mode=${renderMode}`),
  downloadNewPdf: async (id, renderMode = 'customer') => {
    const response = await api.get(`/documents/estimates/${id}/pdf/${renderMode}`, { responseType: 'blob' });
    return response;
  },
  getPreviewHtml: (id, renderMode = 'customer') => 
    api.get(`/documents/estimates/${id}/preview?render_mode=${renderMode}`),
};

// Project APIs
export const projectApi = {
  getAll: (params) => api.get('/projects', { params }),
  getOne: (id) => api.get(`/projects/${id}`),
  create: (data) => api.post('/projects', data),
  update: (id, data) => api.put(`/projects/${id}`, data),
  updateVatRate: (id, vatRate) => api.patch(`/projects/${id}/vat-rate`, { vat_rate: vatRate }),
  updateProjectManager: (id, userId) => api.put(`/projects/${id}/project-manager`, { user_id: userId }),
  delete: (id) => api.delete(`/projects/${id}`),
  updateMilestone: (projectId, milestoneIndex, status) => 
    api.put(`/projects/${projectId}/milestone/${milestoneIndex}`, null, { params: { status } }),
  // Building Practice (Pratica Edilizia)
  getBuildingPractice: (projectId) => api.get(`/projects/${projectId}/building-practice`),
  updateBuildingPractice: (projectId, data) => api.put(`/projects/${projectId}/building-practice`, data),
  validateBuildingPractice: (projectId) => api.post(`/projects/${projectId}/building-practice/validate`),
};

// SAL APIs
export const salApi = {
  getByProject: (projectId) => api.get(`/projects/${projectId}/sal`),
  // READ-ONLY derived analytics (dashboard Avanzamento · Riepilogo). Nessun side-effect.
  getProgressAnalytics: (projectId) => api.get(`/projects/${projectId}/progress-analytics`),
  getContractSummary: (projectId) => api.get(`/projects/${projectId}/sal-contract-summary`),
  update: (salItemId, data) => api.put(`/sal/${salItemId}`, data),
  // R.5: exclude ora accetta payload strutturato (category, reason, economic_effect, replacement_extra_id, stralcio_scope)
  exclude: (salItemId, payload) => {
    // Backward compat: se payload è stringa, la trattiamo come reason legacy (BLOCCATA dal backend per mancanza categoria)
    const body = typeof payload === 'string' ? { reason: payload } : payload;
    return api.put(`/sal/${salItemId}/exclude`, body);
  },
  reactivate: (salItemId, reason = null) => api.put(`/sal/${salItemId}/reactivate`, reason ? { reason } : {}),
  getTimeline: (salItemId) => api.get(`/sal/${salItemId}/timeline`),
  // STEP 5B-2b · stralcio parziale quantità (preview read-only + applicazione)
  previewQuantityStralcio: (salItemId, data) => api.post(`/sal/${salItemId}/stralcio-quantita/preview`, data),
  applyQuantityStralcio: (salItemId, data) => api.post(`/sal/${salItemId}/stralcio-quantita`, data),
  // STEP 5C · rettifica POSITIVA quantità/misure (preview read-only + applicazione). Backend autoritativo su direzione.
  previewQuantityIncrease: (salItemId, data) => api.post(`/sal/${salItemId}/rettifica-quantita/preview`, data),
  applyQuantityIncrease: (salItemId, data) => api.post(`/sal/${salItemId}/rettifica-quantita`, data),

  // Phase C · nuova sottoriga misura (source=extra)
  previewMeasurementLineAdd: (salItemId, data) => api.post(`/sal/${salItemId}/measurement-lines/preview`, data),
  applyMeasurementLineAdd: (salItemId, data) => api.post(`/sal/${salItemId}/measurement-lines`, data),
};

// STRALCIO GOVERNANCE · review gestionale stralci
export const variationReviewApi = {
  list: (projectId) => api.get(`/projects/${projectId}/variation-reviews`),
  requestInfo: (cvId, message) => api.post(`/variation-reviews/${cvId}/request-info`, { message }),
  reply: (cvId, message) => api.post(`/variation-reviews/${cvId}/reply`, { message }),
  verify: (cvId) => api.post(`/variation-reviews/${cvId}/verify`),
};

// Extra APIs
// Extra Storno / Correction APIs (Phase 2B: solo preflight + create draft + read)
export const correctionApi = {
  preflight: (extraId, correctionType = 'full') => api.get(`/extras/${extraId}/corrections/preflight`, { params: { correction_type: correctionType } }),
  preflightPost: (extraId, payload) => api.post(`/extras/${extraId}/corrections/preflight`, payload),
  create: (extraId, payload) => api.post(`/extras/${extraId}/corrections`, payload),
  list: (extraId) => api.get(`/extras/${extraId}/corrections`),
  getOne: (correctionId) => api.get(`/extra-corrections/${correctionId}`),
  generatePdf: (correctionId) => api.post(`/extra-corrections/${correctionId}/generate-pdf`, null, { responseType: 'blob' }),
  signedPdf: (correctionId) => api.get(`/extra-corrections/${correctionId}/signed-pdf`, { responseType: 'blob' }),
  uploadSigned: (correctionId, file) => { const fd = new FormData(); fd.append('file', file); return api.post(`/extra-corrections/${correctionId}/upload-signed`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); },
  applyEffective: (correctionId) => api.post(`/extra-corrections/${correctionId}/apply-effective`),
};

export const extraApi = {
  getByProject: (projectId) => api.get(`/projects/${projectId}/extras`),
  getOne: (extraId) => api.get(`/extras/${extraId}`),
  // STEP 5B-1 · policy pricing LAVORI del progetto (coefficienti per categoria)
  getLavoriPricingPolicy: (projectId) => api.get(`/projects/${projectId}/lavori-pricing-policy`),
  create: (data) => api.post('/extras', data),
  update: (id, data) => api.put(`/extras/${id}`, data),
  // Correggi classificazione (Admin) — Extra firmati
  reclassifyPreflight: (id) => api.get(`/extras/${id}/reclassify/preflight`),
  reclassify: (id, data) => api.post(`/extras/${id}/reclassify`, data),
  // Nuovo flusso
  request: (data) => api.post('/extras/request', data),  // Tecnico richiede extra
  requestModification: (extraId, notes) => api.post(`/extras/${extraId}/request-modification`, null, { params: { modification_notes: notes } }),
  validate: (extraId) => api.post(`/extras/${extraId}/validate`),
  applyDiscount: (extraId, discountPercentage) => api.post(`/extras/${extraId}/apply-discount`, null, { params: { discount_percentage: discountPercentage } }),
  // Flusso approvazione
  generatePdf: (extraId) => api.post(`/extras/${extraId}/generate-pdf`, {}, { responseType: 'blob' }),
  sendToClient: (extraId) => api.post(`/extras/${extraId}/send-to-client`),
  uploadSigned: (extraId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/extras/${extraId}/upload-signed`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getSignedPdfUrl: (extraId) => `${API_URL}/extras/${extraId}/signed-pdf`,
  uploadSupplierDocs: (extraId, file, docType) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('doc_type', docType);
    return api.post(`/extras/${extraId}/upload-supplier-docs`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getSupplierDocUrl: (extraId, docType) => `${API_URL}/extras/${extraId}/supplier-doc/${docType}`,
  approve: (extraId) => api.post(`/extras/${extraId}/approve`),
  reject: (extraId, reason) => api.post(`/extras/${extraId}/reject`, { reason }),
  complete: (extraId) => api.post(`/extras/${extraId}/complete`),
  // STEP 4E.2 · Scelte cliente / Finiture
  markSelectionDone: (extraId, data) => api.post(`/extras/${extraId}/mark-selection-done`, data || {}),
};

// STEP 4E.2 · Supplier picker (read-only, safe projection)
export const supplierApi = {
  picker: (params) => api.get('/suppliers/picker', { params }),
};

// Contract Variation APIs (STEP 4D — wrapper unitario Extra+ / Diminuzioni)
export const contractVariationApi = {
  list: (projectId) => api.get(`/projects/${projectId}/contract-variations`),
  getOne: (variationId) => api.get(`/contract-variations/${variationId}`),
  create: (projectId, data) => api.post(`/projects/${projectId}/contract-variations`, data),
  attach: (variationId, kind, componentId) => api.post(`/contract-variations/${variationId}/attach`, { kind, component_id: componentId }),
  detach: (variationId, kind, componentId) => api.post(`/contract-variations/${variationId}/detach`, { kind, component_id: componentId }),
  makeEffective: (variationId) => api.post(`/contract-variations/${variationId}/effective`),
  cancel: (variationId) => api.post(`/contract-variations/${variationId}/cancel`),
};

// PHASE 3 · Client ↔ Subcontract reconciliation
export const subcontractReconciliationApi = {
  getImpact: (projectId, variationId) =>
    api.get(`/projects/${projectId}/contract-variations/${variationId}/subcontract-impact`),
  preview: (projectId, variationId, data) =>
    api.post(`/projects/${projectId}/contract-variations/${variationId}/subcontract-reconciliation/preview`, data),
  create: (projectId, variationId, data) =>
    api.post(`/projects/${projectId}/contract-variations/${variationId}/subcontract-reconciliation`, data),
  apply: (projectId, linkId) =>
    api.post(`/projects/${projectId}/subcontract-reconciliation/${linkId}/apply`),
  cancel: (projectId, linkId) =>
    api.post(`/projects/${projectId}/subcontract-reconciliation/${linkId}/cancel`),
};

// Suspension APIs
export const suspensionApi = {
  getByProject: (projectId) => api.get(`/projects/${projectId}/suspensions`),
  create: (data) => api.post('/suspensions', data),
  update: (id, data) => api.put(`/suspensions/${id}`, data),
};

// Room APIs
export const roomApi = {
  getAll: () => api.get('/rooms'),
  create: (data) => api.post('/rooms', data),
  update: (id, data) => api.put(`/rooms/${id}`, data),
  delete: (id) => api.delete(`/rooms/${id}`),
  getAvailability: (id, date) => api.get(`/rooms/${id}/availability`, { params: { date } }),
};

// Room Booking APIs
export const roomBookingApi = {
  getAll: (params) => api.get('/room-bookings', { params }),
  create: (data) => api.post('/room-bookings', data),
  delete: (id) => api.delete(`/room-bookings/${id}`),
};

// Contract APIs
export const contractApi = {
  getAll: (status) => api.get('/contracts', { params: { status } }),
  getOne: (id) => api.get(`/contracts/${id}`),
  create: (data) => api.post('/contracts', data),
  update: (id, data) => api.put(`/contracts/${id}`, data),
  updateVatRate: (id, vatRate) => api.patch(`/contracts/${id}/vat-rate`, { vat_rate: vatRate }),
  delete: (id) => api.delete(`/contracts/${id}`),
  uploadDocument: (id, formData) => api.post(`/contracts/${id}/upload-document`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteDocument: (contractId, documentId) => api.delete(`/contracts/${contractId}/documents/${documentId}`),
  submitForValidation: (id) => api.post(`/contracts/${id}/submit-for-validation`),
  validate: (id, status, notes) => api.post(`/contracts/${id}/validate`, null, { 
    params: { status, notes } 
  }),
  // Doppia validazione
  validateComputo: (id, approved, notes) => api.post(`/contracts/${id}/validate-computo`, null, {
    params: { approved, notes }
  }),
  validateAdminDocs: (id, approved, notes) => api.post(`/contracts/${id}/validate-admin-docs`, null, {
    params: { approved, notes }
  }),
  downloadDocument: (contractId, documentId) => `${API_URL}/contracts/${contractId}/documents/${documentId}/download`,
  getPdf: (id) => api.get(`/contracts/${id}/pdf`, { responseType: 'blob' }),
  // Generazione Contratto Appalto
  generateAppalto: (id, data) => api.post(`/contracts/${id}/generate-appalto`, data, { responseType: 'blob' }),
  previewAppalto: (id, data) => api.post(`/contracts/${id}/preview-appalto`, data),
  getTemplateSettings: () => api.get('/settings/contract-template'),
};

// Notification APIs
export const notificationApi = {
  getAll: (unreadOnly) => api.get('/notifications', { params: { unread_only: unreadOnly } }),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
};

// Dashboard APIs
export const dashboardApi = {
  getStats: () => api.get('/dashboard/stats'),
  getSummary: () => api.get('/dashboard/summary'),
  getDirezione: () => api.get('/dashboard/direzione'),
  getAmministrazione: () => api.get('/dashboard/amministrazione'),
  getPm: () => api.get('/dashboard/pm'),
  getTecnico: () => api.get('/dashboard/tecnico'),
};

// Coda globale Richieste di fatturazione (admin/amministrazione)
export const billingQueueApi = {
  getAll: (params) => api.get('/billing-queue', { params }),
  getStats: () => api.get('/billing-queue/stats'),
  reconcile: (dryRun = true) => api.post('/admin/billing-queue/reconcile', null, { params: { dry_run: dryRun } }),
};

// Commission (Provvigioni) APIs
export const commissionApi = {
  getAll: (params) => api.get('/commissions', { params }),
  getSummary: () => api.get('/commissions/summary'),
  getOne: (id) => api.get(`/commissions/${id}`),
  updateStatus: (id, newStatus, invoiceNumber, invoiceDate, notes) => 
    api.put(`/commissions/${id}/status`, null, { 
      params: { 
        new_status: newStatus,
        invoice_number: invoiceNumber,
        invoice_date: invoiceDate,
        notes 
      } 
    }),
};

// Audit Log APIs
export const auditLogApi = {
  getAll: (params) => api.get('/audit-logs', { params }),
};

// Email Template Settings APIs
export const emailTemplateApi = {
  getSettings: () => api.get('/settings/email-template'),
  updateSettings: (data) => api.put('/settings/email-template', data),
  getPreview: (templateType) => api.post(`/settings/email-template/preview?template_type=${templateType}`),
  sendTest: (templateType, recipientEmail) => 
    api.post(`/settings/email-template/send-test?template_type=${templateType}&recipient_email=${encodeURIComponent(recipientEmail)}`),
};

// Email Templates CRUD API (Template Configurabili)
export const emailTemplatesApi = {
  // List & Get
  list: (params) => api.get('/settings/email-templates', { params }),
  get: (id) => api.get(`/settings/email-templates/${id}`),
  
  // CRUD
  create: (data) => api.post('/settings/email-templates', data),
  update: (id, data) => api.put(`/settings/email-templates/${id}`, data),
  delete: (id, hardDelete = false) => api.delete(`/settings/email-templates/${id}`, { params: { hard_delete: hardDelete } }),
  
  // Seed & Variables
  seedDefaults: () => api.post('/settings/email-templates/seed-defaults'),
  variables: (category) => api.get('/settings/email-templates/variables', { params: { category } }),
  
  // Rendering
  render: (id, context) => api.post(`/settings/email-templates/${id}/render`, context ? { context } : {}),
  previewByKey: (templateKey, context) => api.post('/settings/email-templates/preview-by-key', { template_key: templateKey, context }),
  mockContext: (templateKey) => api.get(`/settings/email-templates/mock-context/${templateKey}`),
  
  // Test Email
  sendTest: (id, recipientEmail, context) => api.post(`/settings/email-templates/${id}/send-test-v2`, { recipient_email: recipientEmail, context }),
};

// Email Workflow Rules API (Regole Invio Email)
export const emailRulesApi = {
  list: (params) => api.get('/settings/email-rules', { params }),
  get: (id) => api.get(`/settings/email-rules/${id}`),
  options: () => api.get('/settings/email-rules/options'),
  create: (data) => api.post('/settings/email-rules', data),
  update: (id, data) => api.put(`/settings/email-rules/${id}`, data),
  delete: (id) => api.delete(`/settings/email-rules/${id}`),
  seedDefaults: () => api.post('/settings/email-rules/seed-defaults'),
};

// Init Data API
export const initApi = {
  initData: () => api.post('/init-data'),
};

// Health Check
export const healthApi = {
  check: () => api.get('/health'),
};

// Document Engine API
export const documentApi = {
  // Generate document
  generateEstimate: (estimateId, renderMode = 'customer') => 
    api.post(`/documents/estimates/${estimateId}/generate?render_mode=${renderMode}`),
  
  // Download PDF
  downloadPdf: async (estimateId, renderMode = 'customer') => {
    const response = await api.get(`/documents/estimates/${estimateId}/pdf/${renderMode}`, { responseType: 'blob' });
    return response;
  },
  
  // Get HTML preview
  getPreview: (estimateId, renderMode = 'customer') => 
    api.get(`/documents/estimates/${estimateId}/preview?render_mode=${renderMode}`, { responseType: 'text' }),
  
  // List document instances
  listInstances: (params) => api.get('/documents/instances', { params }),
  
  // Download specific instance
  downloadInstance: async (docId) => {
    const response = await api.get(`/documents/instances/${docId}/download`, { responseType: 'blob' });
    return response;
  },
  
  // Legal blocks
  getLegalBlocks: (documentType = 'estimate') => 
    api.get(`/documents/legal-blocks?document_type=${documentType}`),
  updateLegalBlock: (blockId, data) => api.put(`/documents/legal-blocks/${blockId}`, data),
};

// ==================== CRM DOCUMENTS (Google Drive Storage) ====================
export const crmDocumentsApi = {
  // Config
  getConfig: () => api.get('/documents/config'),
  
  // Upload
  upload: (file, data) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('customer_id', data.customer_id);
    formData.append('document_type', data.document_type);
    if (data.notes) formData.append('notes', data.notes);
    return api.post('/documents/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  
  // List & Get
  list: (params) => api.get('/documents', { params }),
  getOne: (id) => api.get(`/documents/${id}`),
  
  // Download & Preview
  download: async (id) => {
    const response = await api.get(`/documents/${id}/download`, { responseType: 'blob' });
    return response;
  },
  getPreview: (id) => api.get(`/documents/${id}/preview`),
  
  // Update, Rename & Delete
  update: (id, data) => api.put(`/documents/${id}`, data),
  rename: (id, newFilename) => api.put(`/documents/${id}/rename`, { new_filename: newFilename }),
  delete: (id, permanent = false) => api.delete(`/documents/${id}`, { params: { permanent } }),
  
  // Customer-specific
  getByLead: (customerId) => api.get(`/customers/${customerId}/documents`),
  getByCustomer: (customerId) => api.get(`/customers/${customerId}/documents`),
  createFolder: (customerId) => api.post(`/customers/${customerId}/documents/folder`),
};

// Google Calendar APIs
export const googleCalendarApi = {
  // OAuth
  getConnectUrl: () => api.get('/auth/google-calendar/connect'),
  disconnect: () => api.post('/auth/google-calendar/disconnect'),
  getStatus: () => api.get('/auth/google-calendar/status'),
  
  // Sync - usati da CalendarPage.js
  syncAppointment: (appointmentId) => api.post(`/google-calendar/sync-event?appointment_id=${appointmentId}`),
  syncAll: () => api.post('/google-calendar/sync-all'),
  
  // Legacy alias (per retrocompatibilità)
  syncEventToGoogle: (appointmentId) => api.post(`/google-calendar/sync-event?appointment_id=${appointmentId}`),
  
  // Fetch events
  getGoogleEvents: (startDate, endDate) => api.get('/google-calendar/events', { params: { start_date: startDate, end_date: endDate } }),
  importEvent: (eventData) => api.post('/google-calendar/import-event', eventData),
};

export default api;
